import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { exerciseAPI } from '../../services/api';
import toast from 'react-hot-toast';

// Async thunks
export const fetchExercises = createAsyncThunk(
  'exercises/fetchExercises',
  async (_, { rejectWithValue }) => {
    try {
      const response = await exerciseAPI.getExercises();
      // API returns { data: [...], pagination: {...} }
      return response.data?.data || response.data?.exercises || [];
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch exercises');
    }
  }
);

export const createExercise = createAsyncThunk(
  'exercises/createExercise',
  async (exerciseData, { rejectWithValue }) => {
    try {
      const response = await exerciseAPI.createExercise(exerciseData);
      toast.success('Exercise created successfully!');
      return response.data.exercise;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to create exercise';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const updateExercise = createAsyncThunk(
  'exercises/updateExercise',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await exerciseAPI.updateExercise(id, data);
      toast.success('Exercise updated successfully!');
      return response.data.exercise;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update exercise';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const deleteExercise = createAsyncThunk(
  'exercises/deleteExercise',
  async (id, { rejectWithValue }) => {
    try {
      await exerciseAPI.deleteExercise(id);
      toast.success('Exercise deleted successfully!');
      return id;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete exercise';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const exerciseSlice = createSlice({
  name: 'exercises',
  initialState: {
    items: [],
    loading: false,
    error: null,
    selectedExercise: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedExercise: (state, action) => {
      state.selectedExercise = action.payload;
    },
    clearSelectedExercise: (state) => {
      state.selectedExercise = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch exercises
      .addCase(fetchExercises.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExercises.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.error = null;
      })
      .addCase(fetchExercises.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Create exercise
      .addCase(createExercise.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createExercise.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
        state.error = null;
      })
      .addCase(createExercise.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update exercise
      .addCase(updateExercise.fulfilled, (state, action) => {
        const index = state.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.selectedExercise?.id === action.payload.id) {
          state.selectedExercise = action.payload;
        }
      })
      
      // Delete exercise
      .addCase(deleteExercise.fulfilled, (state, action) => {
        state.items = state.items.filter(item => item.id !== action.payload);
        if (state.selectedExercise?.id === action.payload) {
          state.selectedExercise = null;
        }
      });
  },
});

export const { clearError, setSelectedExercise, clearSelectedExercise } = exerciseSlice.actions;

// Selectors
export const selectExercises = (state) => state.exercises.items;
export const selectExercisesLoading = (state) => state.exercises.loading;
export const selectExercisesError = (state) => state.exercises.error;
export const selectSelectedExercise = (state) => state.exercises.selectedExercise;

export default exerciseSlice.reducer;
